// CompareElectricalView - Electrical section with table/2d/3d views
import { useEffect, useState } from 'preact/hooks'
import { COMPARE_ELECTRICAL_SECTION } from '../../../data/definitions.js'
import { getCellNormalized } from '../../../modules/dataService.js'
import { compareCells, hiddenCells } from '../../../state/filterStore.js'
import { activeElectricalSubSection, domain } from '../../../state/globalSignals.js'
import DimensionViewMultiCell from '../plots/DimensionViewMultiCell.jsx'
import { COMPARE_COLORS } from './ComparePlotsView.jsx'

// SVG Icons
const EnergyIcon = () => (
  <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
    <polygon points="13 2 3 14 12 14 11 22 21 10 12 10 13 2"></polygon>
  </svg>
)

const PowerIcon = () => (
  <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
    <polyline points="23 6 13.5 15.5 8.5 10.5 1 18"></polyline>
    <polyline points="17 6 23 6 23 12"></polyline>
  </svg>
)

const VoltageIcon = () => (
  <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
    <polyline points="22 12 18 12 15 21 9 3 6 12 2 12"></polyline>
  </svg>
)

const ACImpedanceIcon = () => (
  <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
    <path d="M21.5 2v6h-6M2.5 22v-6h6M2 11.5a10 10 0 0 1 18.8-4.3M22 12.5a10 10 0 0 1-18.8 4.2"/>
  </svg>
)

const DCResistanceIcon = () => (
  <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
    <rect x="4" y="8" width="16" height="8" rx="1" ry="1"></rect>
    <line x1="2" y1="12" x2="4" y2="12"></line>
    <line x1="20" y1="12" x2="22" y2="12"></line>
  </svg>
)

const InfoIcon = ({ size = 16 }) => (
  <svg width={size} height={size} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
    <circle cx="12" cy="12" r="10"></circle>
    <line x1="12" y1="16" x2="12" y2="12"></line>
    <line x1="12" y1="8" x2="12.01" y2="8"></line>
  </svg>
)

// Sub-sections for electrical with icons
const ELECTRICAL_SUB_SECTIONS = [
  { id: 'energy', label: 'Energy', icon: <EnergyIcon /> },
  { id: 'power', label: 'Power', icon: <PowerIcon /> },
  { id: 'voltage', label: 'Voltage', icon: <VoltageIcon /> },
  { id: 'ac-impedance', label: 'AC Impedance', icon: <ACImpedanceIcon /> },
  { id: 'dc-resistance', label: 'DCIR', icon: <DCResistanceIcon /> }
]

// Get definition for current subsection (compare-specific, no cell-specific values)
function getDefinitionText(subSection) {
  // Map subsection ID to definition key (using compare-specific definitions)
  const definitionMap = {
    'energy': COMPARE_ELECTRICAL_SECTION.energy,
    'power': COMPARE_ELECTRICAL_SECTION.power,
    'voltage': COMPARE_ELECTRICAL_SECTION.voltage,
    'ac-impedance': COMPARE_ELECTRICAL_SECTION.acImpedance,
    'dc-resistance': COMPARE_ELECTRICAL_SECTION.dcResistance
  }

  const definition = definitionMap[subSection]
  if (!definition) return null

  return {
    title: typeof definition.getTitle === 'function'
      ? definition.getTitle(domain.value)
      : definition.title,
    text: definition.text  // No placeholder replacement needed - already generic
  }
}

// Lazy load table component to avoid circular dependency
let CompareTableViewTable = null
import('./CompareTableView.jsx').then(module => {
  CompareTableViewTable = module.CompareTableViewTable || module.default?.CompareTableViewTable
})

export default function CompareElectricalView({ view = '2d' }) {
  const [cellDataMap, setCellDataMap] = useState({})
  const [loading, setLoading] = useState(true)
  const [isFlipped, setIsFlipped] = useState(false)

  // Load FULL cell data for all compare cells (with arrays for plots)
  useEffect(() => {
    async function loadCells() {
      const cellIds = compareCells.value
      if (!cellIds || cellIds.length === 0) {
        setLoading(false)
        setCellDataMap({})
        return
      }

      setLoading(true)
      try {
        console.log('[CompareElectricalView] Loading full cell data for plots:', cellIds)

        // Load cells in parallel with full data (no array stripping)
        const promises = cellIds.map(async (cellId) => {
          const fullData = await getCellNormalized(cellId, { lite: false, fieldGating: 'demo' }) // Compare = browse: server-gate premium for demo
          return {
            productId: cellId,
            manufacturer: fullData?.manufacturer || 'Unknown',
            name: fullData?.name || 'Unknown',
            data: fullData // Full normalized data with all arrays
          }
        })

        const cellsArray = await Promise.all(promises)

        // Convert to map
        const dataMap = {}
        cellsArray.forEach(cell => {
          dataMap[cell.productId] = cell
        })

        console.log('[CompareElectricalView] Loaded cells:', Object.keys(dataMap).length)
        console.log('[CompareElectricalView] Sample data structure:', cellsArray[0])

        setCellDataMap(dataMap)
      } catch (err) {
        console.error('[CompareElectricalView] Failed to load compare cells:', err)
      } finally {
        setLoading(false)
      }
    }

    loadCells()
  }, [compareCells.value])

  // Convert map to array for components, filter out hidden cells
  const allCellDataArray = Object.values(cellDataMap).filter(Boolean)
  const cellDataArray = allCellDataArray.filter(cell => !hiddenCells.value.includes(cell.productId))

  // Loading state
  if (loading) {
    return (
      <div className="compare-section-view loading">
        <div className="loading-spinner">Loading electrical data...</div>
      </div>
    )
  }

  // Empty state
  if (allCellDataArray.length === 0) {
    return (
      <div className="compare-section-view empty">
        <div className="empty-state">
          <p>No cells selected for comparison</p>
          <p className="hint">Select 2-5 cells to compare electrical properties</p>
        </div>
      </div>
    )
  }

  // All cells hidden
  if (cellDataArray.length === 0) {
    return (
      <div className="compare-section-view empty">
        <div className="empty-state">
          <p>All cells are hidden</p>
          <p className="hint">Use the eye icon in the compare panel to show cells</p>
        </div>
      </div>
    )
  }

  // Only 2D/3D views - plots with sub-navigation
  return (
    <div className="compare-section-view compare-section-view-split">
      {/* Left Sidebar: Sub-Section Navigation (Separate Card) */}
      <aside className="compare-subsection-nav compare-subsection-nav-card">
        <div className="nav-header">
          <h3>Electrical Properties</h3>
          <p className="subsection-subtitle">
            Comparing {cellDataArray.length} cells •
            {activeElectricalSubSection.value === 'voltage'
              ? '2D Curve'
              : activeElectricalSubSection.value === 'ac-impedance' && view === '2d'
                ? 'Nyquist Plot'
                : activeElectricalSubSection.value === 'ac-impedance' && view === '3d'
                  ? 'Bode Plots'
                  : view === '2d' ? '2D Curves' : '3D Surfaces'}
          </p>
        </div>

        <div className="subsection-list">
          {ELECTRICAL_SUB_SECTIONS.map(sub => (
            <button
              key={sub.id}
              className={`subsection-btn ${activeElectricalSubSection.value === sub.id ? 'active' : ''}`}
              onClick={() => { activeElectricalSubSection.value = sub.id; setIsFlipped(false) }}
            >
              <div className="category-icon">{sub.icon}</div>
              <div className="category-info">
                <div className="category-label">{sub.label}</div>
              </div>
            </button>
          ))}
        </div>
      </aside>

      {/* Main Content Area (Flipcard) */}
      <div className={`compare-subsection-content compare-subsection-flipcard flipcard ${isFlipped ? 'flipped' : ''}`}>
        <div className="flipcard-inner">
          {/* Front: Plot */}
          <div className="flipcard-front compare-flipcard-front">
            <button className="flip-btn" onClick={() => setIsFlipped(true)} title="Show definition">?</button>

            {/* Guide Question - Prominent header with dynamic title from definitions */}
            <h3 className="column-title">
              {getDefinitionText(activeElectricalSubSection.value)?.title || (
                <>
                  {activeElectricalSubSection.value === 'energy' && 'How does energy capacity vary with power and temperature?'}
                  {activeElectricalSubSection.value === 'power' && 'How long can the cells sustain different power levels?'}
                  {activeElectricalSubSection.value === 'voltage' && 'What is the discharge voltage curve?'}
                  {activeElectricalSubSection.value === 'ac-impedance' && 'What is the impedance spectrum at 25°C?'}
                  {activeElectricalSubSection.value === 'dc-resistance' && 'How does resistance change with SOC and temperature?'}
                </>
              )}
            </h3>

            {/* Plot Area */}
            <div className="subsection-plot-area">
              <DimensionViewMultiCell
                cellDataArray={cellDataArray}
                category={activeElectricalSubSection.value}
                dimension={view}
              />
            </div>

            {/* Legend (show all cells, gray out hidden ones) */}
            <div className="subsection-legend">
              <div className="legend-title">Cells:</div>
              <div className="legend-items">
                {allCellDataArray.map((cell, idx) => {
                  const isHidden = hiddenCells.value.includes(cell.productId)
                  return (
                    <div key={cell.productId} className={`legend-item ${isHidden ? 'legend-hidden' : ''}`}>
                      <span
                        className="legend-color"
                        style={`background: ${COMPARE_COLORS[idx % COMPARE_COLORS.length]}; opacity: ${isHidden ? 0.3 : 1}`}
                      ></span>
                      <span className="legend-label">
                        {cell.manufacturer} {cell.name}
                        {isHidden && <span className="legend-hidden-badge"> (hidden)</span>}
                      </span>
                    </div>
                  )
                })}
              </div>
            </div>
          </div>

          {/* Back: Definition */}
          <div className="flipcard-back compare-flipcard-back">
            <button className="flip-btn flip-btn-back" onClick={() => setIsFlipped(false)} title="Close">×</button>
            <h3 className="column-title">
              {getDefinitionText(activeElectricalSubSection.value)?.title || 'Definition'}
            </h3>
            <div className="flip-content">
              {(() => {
                const definition = getDefinitionText(activeElectricalSubSection.value)
                if (!definition) return <p>No definition available.</p>
                return <div dangerouslySetInnerHTML={{ __html: definition.text }} />
              })()}
            </div>
          </div>
        </div>
      </div>
    </div>
  )
}
